import {
  Radio,
  Button,
  Select,
  MenuItem,
  TextField,
  RadioGroup,
  Autocomplete,
  FormControlLabel,
  createFilterOptions,
} from "@mui/material";
import { useDispatch } from "react-redux";
import React, { useEffect, useState } from "react";
import usePatients from "pages/patients/usePatients";
import LoadingSpinner from "components/LoadingSpinner";
import FormSelectField from "components/FormFields/FormSelect";
import FormFieldError from "components/common/form_error/form_field_error";
import ModalWrapper from "components/common/modals/modalWrapper/ModalWrapper";
import { fetchManufacturers } from "app/services/dashboard/manufacturerService";
import { setManufacturers } from "app/store/manufacturers/slice/manufacturers.slice";
import { getActiveManufacturerAccounts } from "app/services/manufacturers/manufacturers";

import {
  TOAST_SEVERITY,
  DISCONNECTED_MONITOR_DOWNLOAD_TYPES,
} from "static/constants";
import { showToast } from "utils/toast";
import { sortByFirstLetter } from "utils/string";
import { DISCONNECTED_MONITOR_DISALLOWED_MANUFACTURERS } from "static/manufacturers";

const DisconnectedMonitorModal = ({
  isModalOpen,
  onClose,
  onDownload,
  userAccounts,
}) => {
  const [submitClicked, setSubmitClicked] = useState(false);
  const [selectedManufacturer, setSelectedManufacturer] = useState();
  const [selectedAccount, setSelectedAccount] = useState();
  const { isDownloadingDisconnectedMonitors } = usePatients();
  const [availableManufacturers, setAvailableManufacturers] = useState([]);
  const [filteredAccountsByManufacturer, setFilterAccountsByManufacturer] =
    useState([]);
  const [activeAccounts, setActiveAccounts] = useState([]);
  const [downloadType, setDownloadType] = useState(
    DISCONNECTED_MONITOR_DOWNLOAD_TYPES.disconnectedMonitorList
  );

  const dispatch = useDispatch();

  const filter = createFilterOptions();

  useEffect(() => {
    fetchManufacturerList();
    fetchActiveManufacturerList();
  }, []);

  const fetchActiveManufacturerList = async () => {
    const activeAccounts = await getActiveManufacturerAccounts();
    setActiveAccounts(activeAccounts);
  };

  const fetchManufacturerList = async () => {
    try {
      const manufacturers = await fetchManufacturers();
      const availableManufacturers = manufacturers.filter(
        (manufacturer) =>
          !DISCONNECTED_MONITOR_DISALLOWED_MANUFACTURERS.includes(
            manufacturer.name
          )
      );
      dispatch(setManufacturers(manufacturers));
      setAvailableManufacturers(availableManufacturers);
    } catch (error) {
      showToast({ message: error.message, type: TOAST_SEVERITY.ERROR });
    }
  };

  const handleGenerate = () => {
    setSubmitClicked(true);
    if (!selectedManufacturer || !selectedAccount) {
      return;
    }
    const selectedOptions = {
      manufacturer: selectedManufacturer,
      account: selectedAccount,
    };
    onDownload(selectedOptions, downloadType);
  };

  const getOnboardAccountsByManufacturer = (manufacturer) => {
    const filteredByManufacturer = activeAccounts?.data.filter(
      (data) => data.manufacturer === manufacturer
    );
    const filteredAccounts = filteredByManufacturer?.map((acc) => acc?.account);
    const filteredUserAccounts = filteredAccounts?.filter((account) =>
      userAccounts.includes(account)
    );

    return [...new Set(sortByFirstLetter(filteredUserAccounts))];
  };

  return (
    <ModalWrapper
      show={isModalOpen}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      title={
        <div className="d-flex align-items-center">
          <div className="d-flex flex-column">
            <h3>Disconnected Monitors</h3>
          </div>
        </div>
      }
    >
      {isDownloadingDisconnectedMonitors ? (
        <LoadingSpinner />
      ) : (
        <div className="d-flex flex-column" style={{ gap: 10 }}>
          <>
            <div className="mt-2">
              <b>Select the options to generate</b>
              <RadioGroup
                row={true}
                aria-label="option"
                value={downloadType}
                onChange={(e, value) => {
                  setDownloadType(value);
                }}
                name="radio-buttons-group"
              >
                <FormControlLabel
                  value={
                    DISCONNECTED_MONITOR_DOWNLOAD_TYPES.disconnectedMonitorList
                  }
                  control={<Radio />}
                  label="Disconnected Monitor List"
                />
                <FormControlLabel
                  value={DISCONNECTED_MONITOR_DOWNLOAD_TYPES.noShowMasterList}
                  control={<Radio />}
                  label="No-Show Master List"
                />
              </RadioGroup>
            </div>

            <FormSelectField
              label="Manufacturer"
              required={true}
              submitClicked={submitClicked}
              hasError={!selectedManufacturer && submitClicked}
              customMapping={
                <Select
                  value={selectedManufacturer}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedManufacturer(value);
                    setSubmitClicked(false);

                    const filteredAccounts =
                      getOnboardAccountsByManufacturer(value);
                    setFilterAccountsByManufacturer(filteredAccounts);

                    if (filteredAccounts.length === 1) {
                      setSelectedAccount(filteredAccounts[0]);
                    }
                  }}
                >
                  {availableManufacturers.map((manufacturer) => {
                    return (
                      <MenuItem key="" value={manufacturer.name}>
                        {manufacturer.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              }
              margin="normal"
            />

            <Autocomplete
              name="account"
              fullWidth
              clearOnBlur
              selectOnFocus
              handleHomeEndKeys
              id="free-solo-with-text-demo"
              value={selectedAccount}
              disabled={!selectedManufacturer}
              options={
                filteredAccountsByManufacturer?.filter((account) => account) ||
                []
              }
              getOptionLabel={(option) => option}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);
                return filtered;
              }}
              onChange={(event, newValue) => {
                setSelectedAccount(newValue);
                setSubmitClicked(false);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  error={!selectedAccount && submitClicked}
                  hasError={!selectedAccount && submitClicked}
                  label="Account"
                  variant="outlined"
                  placeholder="Account"
                  InputProps={{
                    ...params.InputProps,
                    readOnly: selectedManufacturer ? false : true,
                  }}
                />
              )}
            />
            {!selectedAccount && submitClicked && (
              <FormFieldError errorMessage={`Account is required.`} />
            )}
          </>
          <div className="mt-2 generate-template-modal__footer">
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => onClose()}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleGenerate}
              disabled={isDownloadingDisconnectedMonitors}
            >
              Generate
            </Button>
          </div>
        </div>
      )}
    </ModalWrapper>
  );
};

export default DisconnectedMonitorModal;
